import { useState, useEffect } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import apiClient from "@/utils/apiClient";
import { useAuth } from "@/App";
import { BookOpen, Video, Headphones, Award, ShoppingCart, Check, Loader2, ArrowRight, Gift, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

const typeIcons = {
  ebook: BookOpen,
  webinar: Video,
  podcast: Headphones
};
const typeColors = {
  ebook: "bg-spa-terracotta/10 text-spa-terracotta",
  webinar: "bg-spa-sage/10 text-spa-sage",
  podcast: "bg-spa-sky/10 text-spa-sky"
};

export default function SuperStore() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [legend, setLegend] = useState(null);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const [filter, setFilter] = useState("all");
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState("store");

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, legRes, libRes] = await Promise.all([
          apiClient.get('/store/catalog'),
          apiClient.get('/store/redemption-legend'),
          apiClient.get('/store/my-library').catch(() => ({ data: { items: [] } }))
        ]);
        setCatalog(catRes.data.items || []);
        setLegend(legRes.data);
        setLibrary(libRes.data.items || []);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleRedeem = async (itemId) => {
    setRedeeming(itemId);
    setResult(null);
    try {
      const res = await apiClient.post('/store/redeem', { item_id: itemId });
      setResult({ type: "success", msg: res.data.message, balance: res.data.new_balance });
      setLibrary(prev => [{ item_id: itemId, item_title: catalog.find(i => i.item_id === itemId)?.title, redeemed_at: new Date().toISOString() }, ...prev]);
    } catch (err) {
      setResult({ type: "error", msg: err.response?.data?.detail || "Redemption failed" });
    } finally {
      setRedeeming(null);
    }
  };

  const ownedIds = new Set(library.map(l => l.item_id));
  const filtered = filter === "all" ? catalog : catalog.filter(i => i.type === filter);
  const tabs = ["store", "legend", "library"];

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto" data-testid="super-store-page">
        <div className="mb-8">
          <p className="overline-text mb-2">SMAHOO ME.WELL</p>
          <h1 className="font-serif text-2xl sm:text-3xl font-medium text-spa-moss mb-2">Tele-Edu Therapy Super Store</h1>
          <p className="text-spa-text-secondary text-sm">Redeem SMAHOO Coins for branded eBooks, Webinars, and Podcast series by Mr. RAPsoul.R</p>
        </div>

        {/* Balance Card */}
        <div className="spa-card bg-gradient-to-r from-amber-50 to-white border border-amber-200/30 mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="store-balance">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-spa-text-secondary text-xs">Your Balance</p>
              <p className="font-serif text-2xl text-amber-600 font-medium">{user?.smahoo_coins ?? 0} Coins</p>
            </div>
          </div>
          {result && (
            <div className={`text-sm px-4 py-2 rounded-xl ${result.type === "success" ? "bg-spa-sage/10 text-spa-sage" : "bg-red-50 text-red-600"}`} data-testid="store-result">
              {result.msg}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-spa-muted rounded-xl p-1 mb-8 w-fit" data-testid="store-tabs">
          {tabs.map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-5 py-2 rounded-lg text-sm font-medium transition-all capitalize ${tab === t ? "bg-white text-spa-moss shadow-sm" : "text-spa-text-secondary hover:text-spa-moss"}`} data-testid={`tab-${t}`}>
              {t === "legend" ? "Redemption Guide" : t === "library" ? "My Library" : "Store"}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 text-spa-terracotta animate-spin" />
          </div>
        ) : tab === "store" ? (
          <>
            {/* Type Filter */}
            <div className="flex gap-2 mb-6 flex-wrap" data-testid="store-filters">
              {["all", "ebook", "webinar", "podcast"].map(f => (
                <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-sm transition-all capitalize ${filter === f ? "bg-spa-moss text-white" : "bg-white border border-spa-border text-spa-text-secondary hover:border-spa-sage"}`} data-testid={`filter-${f}`}>
                  {f === "all" ? "All Items" : `${f}s`}
                </button>
              ))}
            </div>

            {/* Catalog Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map(item => {
                const Icon = typeIcons[item.type] || BookOpen;
                const owned = ownedIds.has(item.item_id);
                return (
                  <div key={item.item_id} className={`spa-card border transition-all ${owned ? "border-spa-sage/30 bg-spa-sage/5" : "border-spa-border/50 hover:border-spa-terracotta/30 hover:shadow-md"}`} data-testid={`store-item-${item.item_id}`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeColors[item.type]}`}>
                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <span className="text-[10px] uppercase tracking-wider text-spa-text-secondary">{item.category}</span>
                        <h3 className="font-serif text-base font-medium text-spa-moss leading-snug">{item.title}</h3>
                      </div>
                    </div>
                    <p className="text-spa-text-secondary text-sm leading-relaxed mb-4">{item.description}</p>
                    <p className="text-xs text-spa-text-secondary mb-3">By <span className="font-medium text-spa-moss">{item.author}</span></p>
                    <div className="flex items-center justify-between pt-3 border-t border-spa-border/50">
                      <div className="flex items-center gap-1.5">
                        <Award className="w-4 h-4 text-amber-500" />
                        <span className="font-medium text-amber-600 text-sm">{item.coins_price} Coins</span>
                      </div>
                      {owned ? (
                        <span className="flex items-center gap-1 text-spa-sage text-sm font-medium" data-testid={`owned-${item.item_id}`}>
                          <Check className="w-4 h-4" /> Owned
                        </span>
                      ) : (
                        <Button size="sm" onClick={() => handleRedeem(item.item_id)} disabled={redeeming === item.item_id || (user?.smahoo_coins ?? 0) < item.coins_price} className="bg-spa-terracotta hover:bg-spa-terracotta/90 text-white rounded-full text-xs px-4 gap-1" data-testid={`redeem-${item.item_id}`}>
                          {redeeming === item.item_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShoppingCart className="w-3 h-3" />}
                          Redeem
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : tab === "legend" ? (
          /* Redemption Legend Grid */
          <div className="space-y-8" data-testid="redemption-legend">
            {legend && (
              <>
                {/* Memberships */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-5 h-5 text-spa-terracotta" />
                    <h2 className="font-serif text-lg font-medium text-spa-moss">Premium Memberships</h2>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {legend.memberships.map((m, i) => (
                      <div key={`membership-${i}`} className="spa-card border border-spa-terracotta/20 bg-gradient-to-b from-white to-amber-50/30 text-center" data-testid={`legend-membership-${i}`}>
                        <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                          <Award className="w-6 h-6 text-amber-600" />
                        </div>
                        <p className="font-serif text-2xl text-amber-600 font-medium">{m.coins}</p>
                        <p className="text-xs text-spa-text-secondary mb-2">SMAHOO Coins</p>
                        <p className="font-medium text-spa-moss text-sm">{m.label}</p>
                        <p className="text-xs text-spa-text-secondary mt-1">{m.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Services */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <ArrowRight className="w-5 h-5 text-spa-sage" />
                    <h2 className="font-serif text-lg font-medium text-spa-moss">Services & Sessions</h2>
                  </div>
                  <div className="spa-card border border-spa-border/50 overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-spa-border/50">
                          <th className="text-left p-3 text-spa-text-secondary font-medium text-xs uppercase tracking-wider">Service</th>
                          <th className="text-center p-3 text-spa-text-secondary font-medium text-xs uppercase tracking-wider">Coins</th>
                          <th className="text-right p-3 text-spa-text-secondary font-medium text-xs uppercase tracking-wider">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {legend.services.map((s, i) => (
                          <tr key={`service-${i}`} className="border-b border-spa-border/30 last:border-0" data-testid={`legend-service-${i}`}>
                            <td className="p-3 text-spa-moss">{s.label}</td>
                            <td className="p-3 text-center">
                              <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                                <Award className="w-3.5 h-3.5" /> {s.coins}
                              </span>
                            </td>
                            <td className="p-3 text-right text-spa-text-secondary">{s.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pay It Forward */}
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Heart className="w-5 h-5 text-rose-400" />
                    <h2 className="font-serif text-lg font-medium text-spa-moss">Pay-It-Forward</h2>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {legend.pay_it_forward.map((p, i) => (
                      <div key={p.recipient_id || `pif-${p.amount}-${i}`} className="spa-card border border-rose-100 bg-gradient-to-b from-white to-rose-50/30 flex items-start gap-3" data-testid={`legend-pif-${i}`}>
                        <div className="w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Gift className="w-5 h-5 text-rose-400" />
                        </div>
                 <div>
                          <p className="font-medium text-spa-moss text-sm">{p.label}</p>
                          <p className="text-xs text-spa-text-secondary mt-0.5">{p.value}</p>
                          <p className="flex items-center gap-1 text-amber-600 font-medium text-sm mt-1.5">
                            <Award className="w-3.5 h-3.5" /> {p.coins} Coins
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          /* My Library */
          <div data-testid="my-library">
            {library.length > 0 ? (
              <div className="space-y-3">
                {library.map((item, i) => (
                  <div key={item.redemption_id || `lib-${item.item_title}-${i}`} className="spa-card border border-spa-border/50 flex items-center justify-between" data-testid={`library-item-${i}`}>
                    <div>
                      <p className="font-medium text-spa-moss">{item.item_title}</p>
                      <p className="text-xs text-spa-text-secondary">{item.item_type} &middot; {item.coins_spent} coins &middot; {new Date(item.redeemed_at).toLocaleDateString()}</p>
                    </div>
                    <Check className="w-5 h-5 text-spa-sage" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="spa-card border border-spa-border/50 text-center py-12">
                <BookOpen className="w-12 h-12 text-spa-border mx-auto mb-3" />
                <p className="text-spa-text-secondary">No items yet. Browse the store to redeem your first item!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
